import type { ReactNode } from 'react';

/**
 * MarketingDisplayH1 — canonical display H1 for every marketing surface.
 *
 * Sprint 62 (overnight cycle-1) — unifies four competing display fonts that
 * had drifted across marketing pages (Inter 82px landing, Lora 96px
 * /for-trainers, Inter font-black on pricing/coaches/clubs). The reference
 * treatment is the /for-trainers Sprint 61.19 Lora hero — this component
 * matches that spec verbatim and is the only display H1 to use on marketing
 * pages going forward.
 *
 * Tone:
 *   - "dark" (default) — `text-neutral-900`, for light-background sections
 *   - "light"          — white text + dimmed eyebrow, for navy / chalk-dark heroes
 *
 * The two tones share identical type metrics (Lora, weight 700, clamp 56→96,
 * line-height 1.02, tracking -0.02em). Only the color layer changes.
 */
interface MarketingDisplayH1Props {
  children: ReactNode;
  /** Optional eyebrow above the H1 (uppercase, small, mono). */
  eyebrow?: string;
  /** Alignment — 'left' (default, matches /for-trainers) or 'center' (landing hero). */
  align?: 'left' | 'center';
  /** Tone — 'dark' for light backgrounds (default), 'light' for navy heroes. */
  tone?: 'dark' | 'light';
  /** Optional className passthrough for layout-specific spacing. */
  className?: string;
}

export function MarketingDisplayH1({
  children,
  eyebrow,
  align = 'left',
  tone = 'dark',
  className = '',
}: MarketingDisplayH1Props) {
  const eyebrowColor = tone === 'light' ? 'text-white/55' : 'text-neutral-500';
  const headingColor = tone === 'light' ? 'text-white' : 'text-neutral-900';

  return (
    <header className={`${align === 'center' ? 'text-center' : ''} ${className}`}>
      {eyebrow ? (
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.16em] ${eyebrowColor} mb-3`}
        >
          {eyebrow}
        </p>
      ) : null}
      <h1
        className={`font-bold tracking-tight leading-[1.02] ${headingColor}`}
        style={{
          fontFamily: "var(--font-lora), 'Lora', Georgia, serif",
          fontWeight: 700,
          fontSize: 'clamp(56px, 9vw, 96px)',
          letterSpacing: '-0.02em',
          lineHeight: 1.02,
        }}
      >
        {children}
      </h1>
    </header>
  );
}
